/* eslint-disable custom-rules/no-restricted-imports-clone */
import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Tabs } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { PreferencesContext } from "../providers/PreferencesProvider";
import { IncentivesDistributionListsPanel } from "./IncentivesDistributionListsPanel";
import { FiltersContext } from "../providers/FiltersProvider";
import { SelectVehicles } from "./SelectVehicles";
import { defaultFilters } from "../providers/FiltersProvider";
import type { RootNode } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import type { MapTreeNode } from "../utils/navFilterUtils";
import {
  type Filters,
  getActiveTrimIds,
  getMakeTree,
  getTrimIdsFromFilters,
  setMakeTreeByTrimIds
} from "../utils/navFilterUtils";
import { GeneralDetailsPanel } from "./GeneralDetailsPanel";
import { DetailsPanel } from "./DetailsPanel";
import { useWriteAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import {
  deepCopyObject,
  getIncentiveLogicalState,
  hasObjectChanged
} from "../utils/utils";
import type { EditIncentivesDisableFlags } from "@dealer-center/ddc-pages-incentives-manager-interface";

import "../styles/popups.scss";

interface EditIncentivePopupProps {
  type: string;
  asAccount: string;
  asUser: string;
  defaultSelectedMake?: string;
  currentFilters: Filters;
  getDisableFlags: (
    incentive: IncentivesRecord,
    type: string
  ) => EditIncentivesDisableFlags;
  hasEditChanges: boolean;
  incentive: IncentivesRecord;
  navtreeData: RootNode | null;
  onCancel?: () => void;
  onSave?: () => void;
  isMakeLocked?: (make: string) => boolean | undefined;
  onChange?: (hasChanges: boolean) => void;
}

export const EditIncentivePopup = ({
  type,
  asAccount,
  asUser,
  defaultSelectedMake,
  currentFilters,
  getDisableFlags,
  hasEditChanges,
  incentive,
  navtreeData,
  onCancel,
  onSave,
  isMakeLocked,
  onChange
}: EditIncentivePopupProps) => {
  const { preferences } = useContext(PreferencesContext);
  const { groups } = preferences;
  const contentDefaults = preferences.contentDefaults || [];

  const { dispatch } = useContext(FiltersContext);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishFromState, setPublishFromState] = useState("DRAFT");

  const [initialMakeTree, setInitialMakeTree] = useState<MapTreeNode>({
    active: [],
    labels: [],
    nodes: []
  });

  const [mutableMakeTree, setMutableMakeTree] = useState<MapTreeNode>({
    active: [],
    labels: [],
    nodes: []
  });
  const [condition, setCondition] = useState<string>("");
  const [tabIndex, setTabIndex] = useState(0);
  const [initialIncentive, setInitialIncentive] = useState<IncentivesRecord>(
    deepCopyObject(incentive)
  );
  const [mutableEditIncentive, setMutableEditIncentive] =
    useState<IncentivesRecord>(deepCopyObject(incentive));

  const onTabActivate = (index: number) => {
    setTabIndex(index);
  };
  const changeError = (id: string, status: boolean) => {
    setErrors(prevObject => {
      const updatedErrors = { ...prevObject, [id]: status };
      return updatedErrors;
    });
  };
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const [editError, setEditError] = useState<string>("");
  const disableFlags = getDisableFlags(incentive, type);

  const hasErrors = () => Object.values(errors).some((value: boolean) => value);

  const afterSaveSuccess = data => {
    setEditError("");
    setIsSaving(false);
    dispatch({
      type: "INCENTIVE_CHANGED",
      incentive: data?.[0].incentives?.[0],
      accountId: asAccount,
      lang: Locale.getCurrent().substring(0, 2)
    });
  };

  const afterPublishSuccess = data => {
    setEditError("");
    setIsPublishing(false);
    dispatch({
      type: "MOVE_INCENTIVES",
      incentiveIds: [data?.[0].incentives?.[0].id],
      fromState: publishFromState,
      toState: getIncentiveLogicalState(data?.[0].incentives?.[0], undefined)
    });
  };

  const saveIncentiveMutation = useWriteAPI({
    resource: "save-incentive",
    asAccount,
    asUser,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: afterSaveSuccess
  });

  const doSave = async () => {
    try {
      setIsSaving(true);
      await saveIncentiveMutation.mutateAsync({
        body: mutableEditIncentive as object
      });
      onSave && onSave();
    } catch (error) {
      setEditError(error.message);
      setIsSaving(false);
    }
  };

  const publishIncentiveMutation = useWriteAPI({
    resource: "update-incentives-status",
    asAccount,
    asUser,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: afterPublishSuccess
  });

  const doPublish = async () => {
    try {
      setIsPublishing(true);
      setPublishFromState(
        getIncentiveLogicalState(mutableEditIncentive, undefined)
      );
      const saveIncentiveResponse = await saveIncentiveMutation.mutateAsync({
        body: mutableEditIncentive as object
      });
      const incentiveId = saveIncentiveResponse.body?.[0].incentives[0].id;
      await publishIncentiveMutation.mutateAsync({
        body: [incentiveId] as object,
        extraURLParams: "status=PUBLISHED"
      });
      onSave && onSave();
    } catch (error) {
      setEditError(error.message);
      setIsPublishing(false);
    }
  };

  const languageCode = Locale.getCurrent().split("-")[0];

  const hasChanges = (): boolean => {
    const isChanged =
      hasObjectChanged(initialIncentive, mutableEditIncentive) ||
      hasObjectChanged(initialMakeTree, mutableMakeTree);
    return isChanged;
  };

  useEffect(() => {
    if (
      initialMakeTree.labels.length === 0 &&
      incentive &&
      incentive.applicableMake
    ) {
      const initialTree = getMakeTree(
        navtreeData,
        incentive.applicableMake,
        false
      );
      let initialTrimIds = incentive.globalVehicleTrimIdList || [];
      if (type === "CREATE_INCENTIVE") {
        // Only pre-select items if there are actual Year/Model/Trim filters
        // If only Make is selected, leave everything unchecked
        const hasSpecificFilters =
          currentFilters.year != null || // Handles both null and undefined
          (Array.isArray(currentFilters.years) &&
            currentFilters.years.length > 0) ||
          currentFilters.model != null ||
          currentFilters.trim != null;

        if (hasSpecificFilters) {
          const initialFilters: Filters = {
            ...defaultFilters,
            make: incentive.applicableMake,
            condition: currentFilters.condition
              ? currentFilters.condition
              : "NEW",
            model: currentFilters.model ? currentFilters.model : null,
            trim: currentFilters.trim ? currentFilters.trim : null,
            year: currentFilters.year ? currentFilters.year : null,
            years: currentFilters.years ? currentFilters.years : null,
            type: currentFilters.type ? currentFilters.type : null,
            source: currentFilters.source ? currentFilters.source : null
          };
          initialTrimIds = getTrimIdsFromFilters(initialTree, initialFilters);
        } else {
          // No specific filters - leave everything unchecked
          initialTrimIds = [];
        }
      }
      const makeTree = setMakeTreeByTrimIds(initialTree, initialTrimIds);
      setInitialMakeTree(makeTree);
      const newMakeTree = deepCopyObject(makeTree);
      setMutableMakeTree(newMakeTree);
      const activeTrimIds: string[] = getActiveTrimIds(newMakeTree);
      setInitialIncentive(prevState => ({
        ...prevState,
        globalVehicleTrimIdList: activeTrimIds
      }));
    }
    if (currentFilters.condition) {
      setCondition(currentFilters.condition);
    }
  }, []);

  useEffect(() => {
    if (mutableMakeTree && mutableMakeTree.labels.length > 0) {
      const activeTrimIds: string[] = getActiveTrimIds(mutableMakeTree);
      if (
        hasObjectChanged(
          activeTrimIds,
          mutableEditIncentive.globalVehicleTrimIdList
        )
      ) {
        setMutableEditIncentive(prevState => ({
          ...prevState,
          globalVehicleTrimIdList: activeTrimIds
        }));
      }
    }
  }, [mutableMakeTree, mutableEditIncentive]);

  const tabData = [
    {
      component: (
        <div className="popup-content">
          <SelectVehicles
            condition={condition}
            disableMake={type !== "CREATE_INCENTIVE"}
            disableYearModelTrim={disableFlags.readOnly}
            incentive={mutableEditIncentive}
            isMakeLocked={isMakeLocked}
            makeRequired={true}
            makeTree={mutableMakeTree}
            navtreeData={navtreeData}
            setIncentive={setMutableEditIncentive}
            setMakeTree={setMutableMakeTree}
            type={type}
          />
        </div>
      ),
      label: Locale.getText("SELECT_VEHICLES"),
      value: 0,
      active: tabIndex === 0
    },
    {
      component: (
        <div className="popup-content">
          <DetailsPanel
            changeError={changeError}
            disableFlags={disableFlags}
            incentive={mutableEditIncentive}
            languageCode={languageCode}
            setEditError={setEditError}
            setIncentive={setMutableEditIncentive}
          />
        </div>
      ),
      label: Locale.getText("DETAILS"),
      value: 1,
      active: tabIndex === 1
    },
    {
      component: (
        <div className="popup-content">
          <GeneralDetailsPanel
            changeError={changeError}
            disableFlags={disableFlags}
            incentive={mutableEditIncentive}
            languageCode={languageCode}
            setIncentive={setMutableEditIncentive}
          />
        </div>
      ),
      label: Locale.getText("GENERAL"),
      value: 2,
      active: tabIndex === 2
    },
    {
      component: (
        <div className="popup-content">
          <IncentivesDistributionListsPanel
            disableFlags={disableFlags}
            groups={groups || []}
            incentive={mutableEditIncentive}
            onFlagChange={(groupIndex, status) => {
              const newList =
                mutableEditIncentive?.accountMap?.distributionListIdList || [];
              const newGroups = [...newList];

              const groupId = groups?.[groupIndex]?.id;
              if (!groupId) {
                return;
              }
              const index = newGroups.indexOf(groupId);
              if (status) {
                if (index === -1) {
                  newGroups.push(groupId);
                }
              } else {
                if (index > -1) {
                  newGroups.splice(index, 1);
                }
              }
              setMutableEditIncentive(prevState => ({
                ...prevState,
                accountMap: {
                  ...prevState.accountMap,
                  distributionListIdList: newGroups
                }
              }));
            }}
          />
        </div>
      ),
      label: Locale.getText("DISTRUBUTION_LIST"),
      value: 3,
      active: tabIndex === 3,
      disabled: disableFlags.hideDistributionLists
    }
  ];

  // Move onChange call to useEffect to prevent rendering during render cycle
  useEffect(() => {
    if (onChange && hasEditChanges !== hasChanges()) {
      onChange(!hasEditChanges);
    }
  }, [hasEditChanges, onChange, hasChanges]);

  const isPublishable =
    incentive.accountMap.status === "DRAFT" ||
    incentive.accountMap.status === "DECLINED";

  const saveLabel =
    type === "CLONE_INCENTIVE" || type == "CREATE_INCENTIVE"
      ? "SAVE_AS_DRAFT"
      : "SAVE";

  return (
    <>
      {editError && (
        <Alert
          displayCloseButton={true}
          role="alert"
          type="info"
          onCloseClick={() => setEditError("")}
        >
          {editError}
        </Alert>
      )}
      <Tabs tabs={tabData} onActivate={onTabActivate} />
      <div className="popup-actions">
        {hasEditChanges && (
          <Button
            analyticsConfiguration={{
              eventName: "edit-incentive-reset",
              eventProperties: undefined
            }}
            buttonStyle="tertiary"
            onClick={() => {
              setMutableMakeTree(deepCopyObject(initialMakeTree));
              setMutableEditIncentive(deepCopyObject(initialIncentive));
              onChange && onChange(false);
            }}
          >
            {Locale.getText("RESET")}
          </Button>
        )}
        <Button
          analyticsConfiguration={{
            eventName: "edit-incentive-cancel",
            eventProperties: undefined
          }}
          buttonStyle="tertiary"
          onClick={() => {
            onCancel && onCancel(); // hasChanges());
          }}
        >
          {Locale.getText("CANCEL")}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "edit-incentive-save",
            eventProperties: undefined
          }}
          buttonStyle="primary"
          disabled={
            isPublishing ||
            (mutableEditIncentive.globalVehicleTrimIdList?.length ?? 0) === 0 ||
            hasErrors() ||
            (type !== "CLONE_INCENTIVE" && !hasEditChanges)
          }
          isLoading={isSaving}
          onClick={doSave}
        >
          {Locale.getText(saveLabel)}
        </Button>
        {isPublishable && (
          <Button
            analyticsConfiguration={{
              eventName: "edit-incentive-publish",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={hasErrors() || isSaving}
            isLoading={isPublishing}
            onClick={doPublish}
          >
            {Locale.getText("PUBLISH")}
          </Button>
        )}
      </div>
    </>
  );
};
